/* RESPONSIBLE TEAM: team-reporting */

import { setOwner } from '@ember/application';
import { inject as service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import { task } from 'ember-concurrency-decorators';
import { taskFor } from 'ember-concurrency-ts';
import { type TaskGenerator } from 'ember-concurrency';
import { underscore } from '@ember/string';
import ajax, { get, post, put } from 'embercom/lib/ajax';
import type AppService from 'embercom/services/app-service';
import type Store from '@ember-data/store';
import type ScheduledExternalShare from 'embercom/models/reporting/scheduled-external-share';
import { captureException } from 'embercom/lib/sentry';
import type { SchedulePayload } from 'embercom/types/reporting/scheduled-delivery';

export const DEFAULT_PAGE_SIZE = 50;
export const DEFAULT_PAGE_FROM = 0;

interface SchedulesApiResponse {
  data: Array<Record<string, any>>;
  total_hits: number;
}

export interface SearchParams {
  app_id: number;
  page_from: number;
  page_size: number;
  sort_by?: string;
  sort_direction?: 'asc' | 'desc';
}

export default class ManageSchedulesApi {
  @service declare appService: AppService;
  @service declare store: Store;

  constructor(owner: unknown) {
    setOwner(this, owner);
    this.searchParams = this.getDefaultSearchParams();
  }

  @tracked currentPage = 0;
  @tracked totalCount = 0;
  @tracked lastPageHit?: boolean;
  @tracked schedules: ScheduledExternalShare[] = [];
  @tracked searchParams: SearchParams;
  @tracked previousSchedulesCount = 0;

  get pageSize(): number {
    return this.searchParams?.page_size ?? DEFAULT_PAGE_SIZE;
  }

  get appId(): number {
    return this.appService.app.id;
  }

  reset() {
    this.currentPage = 0;
    this.totalCount = 0;
    this.lastPageHit = undefined;
    this.schedules = [];
    this.searchParams = this.getDefaultSearchParams();
    this.previousSchedulesCount = 0;
  }

  private getDefaultSearchParams(): SearchParams {
    return {
      app_id: this.appId,
      page_from: DEFAULT_PAGE_FROM,
      page_size: DEFAULT_PAGE_SIZE,
    };
  }

  // Restartable task allows new searches to cancel in-flight requests
  @task({ restartable: true })
  private *loadPageTask(): TaskGenerator<void> {
    if (this.lastPageHit !== true) {
      try {
        let params = this.searchParams || this.getDefaultSearchParams();
        let response: SchedulesApiResponse = yield get(
          '/ember/reporting/scheduled_external_share',
          {
            ...params,
            page_from: this.currentPage * this.pageSize,
          },
        );

        this.handleResponse(response);
      } catch (error) {
        captureException(error);
      }
    }
  }

  private handleResponse(response: SchedulesApiResponse) {
    let schedulesData = response.data || [];
    this.totalCount = response.total_hits || 0;

    // Convert raw API data to Ember Data models
    let scheduleModels = this.generateScheduleModels(schedulesData);

    let receivedCount = scheduleModels.length;
    let totalFetched = this.currentPage * this.pageSize + receivedCount;
    this.lastPageHit = totalFetched >= this.totalCount;

    if (!this.lastPageHit) {
      this.currentPage += 1;
    }

    this.schedules = [...this.schedules, ...scheduleModels];
    this.previousSchedulesCount = 0;
  }

  private generateScheduleModels(
    objects: Array<Record<string, any>> = [],
  ): ScheduledExternalShare[] {
    return objects.map((schedule) => {
      let normalizedObject = this.store.normalize('reporting/scheduled-external-share', schedule);
      return this.store.push(normalizedObject) as ScheduledExternalShare;
    });
  }

  get isLoadingPage(): boolean {
    return taskFor(this.loadPageTask).isRunning;
  }

  async loadPage() {
    return taskFor(this.loadPageTask).perform();
  }

  async sortUpdate(sortBy?: string, sortDirection?: 'asc' | 'desc') {
    this.currentPage = 0;
    this.lastPageHit = false;
    this.previousSchedulesCount = this.schedules.length;
    this.schedules = [];

    this.searchParams = {
      ...this.searchParams,
      sort_by: sortBy ? underscore(sortBy) : undefined,
      sort_direction: sortDirection,
    };

    return this.loadPage();
  }

  async deleteSchedule(scheduleId: string): Promise<void> {
    try {
      let url = `/ember/reporting/scheduled_external_share/${scheduleId}?app_id=${this.appId}`;

      await ajax({
        url,
        type: 'DELETE',
        dataType: 'text',
      });

      this.schedules = this.schedules.filter((s) => String(s.id) !== String(scheduleId));
      this.totalCount = Math.max(0, this.totalCount - 1);
    } catch (error) {
      captureException(error, {
        extra: {
          scheduleId,
          appId: this.appId,
          operation: 'deleteSchedule',
        },
      });
      throw error;
    }
  }

  updateScheduleInList(updatedScheduleData: ScheduledExternalShare): void {
    let scheduleIndex = this.schedules.findIndex(
      (s) => String(s.id) === String(updatedScheduleData.id),
    );

    if (scheduleIndex !== -1) {
      this.schedules = [
        ...this.schedules.slice(0, scheduleIndex),
        updatedScheduleData,
        ...this.schedules.slice(scheduleIndex + 1),
      ];
    }
  }

  /**
   * Serializes payload from camelCase to snake_case for API
   */
  private serializePayload(payload: SchedulePayload) {
    return this.decamelizeKeys(payload);
  }

  private decamelizeKeys(obj: any): any {
    if (Array.isArray(obj)) {
      return obj.map((item) => this.decamelizeKeys(item));
    } else if (obj !== null && typeof obj === 'object') {
      return Object.entries(obj).reduce((acc, [key, value]) => {
        acc[underscore(key)] = this.decamelizeKeys(value);
        return acc;
      }, {} as any);
    }
    return obj;
  }

  private throwEnhancedError(error: any): never {
    if (error?.jqXHR?.status === 422) {
      let validationError = error?.jqXHR?.responseJSON?.errors?.[0]?.message;
      if (validationError) {
        let enhancedError = new Error(validationError);
        enhancedError.name = 'ValidationError';
        throw enhancedError;
      }
    }
    throw error;
  }

  async createSchedule(payload: SchedulePayload): Promise<ScheduledExternalShare> {
    try {
      let serializedPayload = this.serializePayload(payload);
      let response = await post('/ember/reporting/scheduled_external_share', serializedPayload);
      let normalized = this.store.normalize('reporting/scheduled-external-share', response);
      return this.store.push(normalized) as ScheduledExternalShare;
    } catch (error) {
      captureException(error, {
        extra: {
          payload,
          operation: 'createSchedule',
        },
      });
      this.throwEnhancedError(error);
    }
  }

  async updateSchedule(
    scheduleId: string,
    payload: SchedulePayload,
  ): Promise<ScheduledExternalShare> {
    try {
      let serializedPayload = this.serializePayload(payload);
      let url = `/ember/reporting/scheduled_external_share/${scheduleId}?app_id=${this.appId}`;
      let response = await put(url, serializedPayload);
      let normalized = this.store.normalize('reporting/scheduled-external-share', response);
      return this.store.push(normalized) as ScheduledExternalShare;
    } catch (error) {
      captureException(error, {
        extra: {
          scheduleId,
          payload,
          appId: this.appId,
          operation: 'updateSchedule',
        },
      });
      this.throwEnhancedError(error);
    }
  }
}
